import React, { useState, useEffect, useCallback } from 'react';
import colaboradorHorarioService from '../../services/colaboradorHorario.service';
import configSistemaService       from '../../services/configSistema.service';
import authService                from '../../services/auth.service';
import SePode                     from '../../components/SePode';   // r88
import BrowseHeader               from '../../components/BrowseHeader';                    // r109
import { iconeDaRotina }          from '../../constants/iconesRotinas';               // r109

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  borda:   '#e8eaed', cinzaBg: '#fafafa',
  texto:   '#1a1a2e', muted:   '#888',
  branco:  '#fff',
};

const DIAS = [
  { v: 1, n: 'Segunda', s: 'Seg' }, { v: 2, n: 'Terça',   s: 'Ter' },
  { v: 3, n: 'Quarta',  s: 'Qua' }, { v: 4, n: 'Quinta',  s: 'Qui' },
  { v: 5, n: 'Sexta',   s: 'Sex' }, { v: 6, n: 'Sábado',  s: 'Sáb' },
  { v: 7, n: 'Domingo', s: 'Dom' },
];

const inputStyle = {
  width: '100%', height: '32px', padding: '0 8px',
  fontSize: '12px', fontFamily: 'inherit',
  border: `1px solid ${COR.borda}`, borderRadius: '4px',
  color: COR.texto, boxSizing: 'border-box',
};

// ════════════════════════════════════════════════════════════════════════════
// HorariosColaboradoresPage — lista colaboradores ativos com seus horários
// (override ou global). Permite editar inline e remover override.
// ════════════════════════════════════════════════════════════════════════════
function HorariosColaboradoresPage() {
  const usuarioLogado = authService.getUsuarioLogado();
  const [colaboradores, setColaboradores] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [global, setGlobal] = useState({});  // defaults globais
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');

  const carregarTudo = useCallback(async () => {
    setCarregando(true);
    try {
      const [list, configs] = await Promise.all([
        colaboradorHorarioService.listar(),
        configSistemaService.listar('horario.'),
      ]);
      setColaboradores(list);
      const map = {};
      configs.forEach(c => { map[c.chave] = c.valor; });
      setGlobal(map);
    } catch (err) {
      console.error(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregarTudo(); }, [carregarTudo]);

  const labelDia = (n) => {
    const d = DIAS.find(x => x.v === n);
    return d ? d.s : '?';
  };

  // Renderiza o resumo do horário (com fallback pro global)
  const resumoHorario = (c) => {
    const ini = c.dia_semana_inicio ?? parseInt(global['horario.dia_semana_inicio'], 10);
    const fim = c.dia_semana_fim    ?? parseInt(global['horario.dia_semana_fim'],    10);
    const hi  = c.hora_inicio       ?? global['horario.hora_inicio'];
    const hf  = c.hora_fim          ?? global['horario.hora_fim'];
    const ai  = c.hora_almoco_ini   ?? global['horario.hora_almoco_ini'];
    const af  = c.hora_almoco_fim   ?? global['horario.hora_almoco_fim'];
    return `${labelDia(ini)}–${labelDia(fim)}, ${hi}–${ai} / ${af}–${hf}`;
  };

  const abrirEdicao = (c) => {
    setEditandoId(c.id);
    setForm({
      dia_semana_inicio: c.dia_semana_inicio ?? global['horario.dia_semana_inicio'] ?? '1',
      dia_semana_fim:    c.dia_semana_fim    ?? global['horario.dia_semana_fim']    ?? '5',
      hora_inicio:       c.hora_inicio       ?? global['horario.hora_inicio']       ?? '09:00',
      hora_almoco_ini:   c.hora_almoco_ini   ?? global['horario.hora_almoco_ini']   ?? '12:00',
      hora_almoco_fim:   c.hora_almoco_fim   ?? global['horario.hora_almoco_fim']   ?? '13:00',
      hora_fim:          c.hora_fim          ?? global['horario.hora_fim']          ?? '18:00',
      observacao:        c.observacao        || '',
    });
    setMsg('');
  };

  const cancelar = () => { setEditandoId(null); setForm({}); };

  const salvar = async (c) => {
    try {
      await colaboradorHorarioService.upsert(c.id, { ...form, usuario_id: usuarioLogado?.id });
      setEditandoId(null);
      await carregarTudo();
      setMsg(`✓ Horário de ${c.nome_amigavel || c.nome} salvo.`);
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg('Erro: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  const removerOverride = async (c) => {
    if (!window.confirm(
      `Remover horário customizado de ${c.nome_amigavel || c.nome}?\n` +
      `Esse colaborador voltará a usar o horário global.`
    )) return;
    try {
      await colaboradorHorarioService.remover(c.id);
      await carregarTudo();
      setMsg(`✓ Override removido. ${c.nome_amigavel || c.nome} agora usa o horário global.`);
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao remover.');
    }
  };

  const filtrados = colaboradores.filter(c => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return (c.nome_amigavel || '').toLowerCase().includes(q)
        || (c.nome || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', fontFamily: "'Manrope', sans-serif" }}>
      {/* r109: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="horarios-colaboradores"
        titulo="Horários por Colaborador"
        descricao="Cada colaborador pode ter seu próprio horário comercial. Sem override = usa o horário global definido nas Configurações do Sistema."
      />

      {/* Card global (referência) */}
      <div style={{
        backgroundColor: COR.azulBg,
        border: `1px solid ${COR.azul}33`,
        borderRadius: '6px',
        padding: '10px 14px',
        marginBottom: '12px',
        fontSize: '12px',
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        <span>🌐</span>
        <div style={{ flex: 1 }}>
          <b style={{ color: COR.azul }}>Horário Global:</b>{' '}
          {labelDia(parseInt(global['horario.dia_semana_inicio'], 10))}
          {'–'}
          {labelDia(parseInt(global['horario.dia_semana_fim'], 10))}
          {', '}
          {global['horario.hora_inicio']}–{global['horario.hora_almoco_ini']}
          {' / '}
          {global['horario.hora_almoco_fim']}–{global['horario.hora_fim']}
        </div>
        <span style={{ color: COR.muted, fontSize: '11px' }}>
          Editável em ⚙ Configurações do Sistema
        </span>
      </div>

      {/* Toolbar */}
      <div style={{
        marginBottom: '10px',
        backgroundColor: COR.branco, padding: '10px 12px', borderRadius: '6px',
        border: `1px solid ${COR.borda}`,
      }}>
        <input
          placeholder="🔍 Buscar colaborador..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ ...inputStyle, height: '34px', fontSize: '13px' }}
        />
      </div>

      {msg && (
        <div style={{
          marginBottom: '10px', padding: '8px 12px',
          backgroundColor: msg.startsWith('✓') ? COR.verdeBg : COR.vermBg,
          color: msg.startsWith('✓') ? COR.verde : COR.vermelho,
          borderRadius: '5px', fontSize: '12px',
          border: `1px solid ${msg.startsWith('✓') ? COR.verde : COR.vermelho}33`,
        }}>{msg}</div>
      )}

      {/* Lista */}
      {carregando ? (
        <div style={{ padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px' }}>
          Carregando...
        </div>
      ) : (
        <div style={{
          backgroundColor: COR.branco, borderRadius: '6px',
          border: `1px solid ${COR.borda}`, overflow: 'hidden',
        }}>
          {/* Cabeçalho */}
          <div style={{
            display: 'grid', gridTemplateColumns: '36px 1fr 1.5fr 110px 110px',
            backgroundColor: COR.azul, padding: '10px 14px',
            fontSize: '13px', fontWeight: '700', color: COR.branco,
          }}>
            <div></div>
            <div>Colaborador</div>
            <div>Horário</div>
            <div>Status</div>
            <div style={{ textAlign: 'right' }}>Ações</div>
          </div>

          {filtrados.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px' }}>
              Nenhum colaborador encontrado.
            </div>
          )}

          {filtrados.map(c => (
            <React.Fragment key={c.id}>
              <div style={{
                display: 'grid', gridTemplateColumns: '36px 1fr 1.5fr 110px 110px',
                padding: '10px 14px', fontSize: '12px', alignItems: 'center',
                borderBottom: editandoId === c.id ? 'none' : `1px solid ${COR.borda}`,
                backgroundColor: editandoId === c.id ? COR.azulBg : COR.branco,
              }}>
                {/* r109: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {iconeDaRotina('horarios-colaboradores', { width: 20, height: 20, color: COR.azul })}
                </div>
                <div style={{ color: COR.texto, fontWeight: '500' }}>
                  {c.nome_amigavel || c.nome}
                  {c.nome_amigavel && (
                    <div style={{ fontSize: '10px', color: COR.muted, marginTop: '2px' }}>
                      {c.nome}
                    </div>
                  )}
                </div>
                <div style={{ color: COR.texto, fontVariantNumeric: 'tabular-nums', fontSize: '11px' }}>
                  {resumoHorario(c)}
                </div>
                <div>
                  {c.tem_override ? (
                    <span style={{
                      display: 'inline-block', padding: '2px 10px',
                      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                      backgroundColor: COR.amlBg, color: COR.amarelo,
                      border: `1px solid ${COR.amarelo}`,         // r109: borda sólida na cor
                    }}>Customizado</span>
                  ) : (
                    <span style={{
                      display: 'inline-block', padding: '2px 10px',
                      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                      backgroundColor: COR.cinzaBg, color: COR.muted,
                      border: `1px solid ${COR.muted}`,           // r109: borda sólida na cor da fonte
                    }}>Global</span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                  <SePode rotina="horarios-colaboradores" acao="alterar">
                  <button onClick={() => abrirEdicao(c)} title="Editar"
                    disabled={editandoId === c.id}
                    style={{
                      border: 'none', background: 'transparent',
                      color: COR.azul,
                      cursor: editandoId === c.id ? 'default' : 'pointer',
                      fontSize: '13px', padding: '2px 6px',
                      opacity: editandoId === c.id ? 0.4 : 1,
                    }}>✏️</button>
                  </SePode>
                  {c.tem_override && (
                    <SePode rotina="horarios-colaboradores" acao="alterar">
                    <button onClick={() => removerOverride(c)} title="Remover override"
                      style={{
                        border: 'none', background: 'transparent',
                        color: COR.vermelho, cursor: 'pointer',
                        fontSize: '13px', padding: '2px 6px',
                      }}>🗑️</button>
                    </SePode>
                  )}
                </div>
              </div>

              {editandoId === c.id && (
                <div style={{
                  padding: '14px 16px', backgroundColor: COR.azulBg,
                  borderBottom: `1px solid ${COR.borda}`,
                }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '10px', marginBottom: '8px',
                  }}>
                    <Campo label="Dia início">
                      <select value={form.dia_semana_inicio}
                        onChange={e => setForm(f => ({ ...f, dia_semana_inicio: e.target.value }))}
                        style={inputStyle}>
                        {DIAS.map(d => <option key={d.v} value={d.v}>{d.n}</option>)}
                      </select>
                    </Campo>
                    <Campo label="Dia fim">
                      <select value={form.dia_semana_fim}
                        onChange={e => setForm(f => ({ ...f, dia_semana_fim: e.target.value }))}
                        style={inputStyle}>
                        {DIAS.map(d => <option key={d.v} value={d.v}>{d.n}</option>)}
                      </select>
                    </Campo>
                    <Campo label="Hora início">
                      <input type="time" value={form.hora_inicio}
                        onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))}
                        style={inputStyle} />
                    </Campo>
                    <Campo label="Hora fim">
                      <input type="time" value={form.hora_fim}
                        onChange={e => setForm(f => ({ ...f, hora_fim: e.target.value }))}
                        style={inputStyle} />
                    </Campo>
                    <Campo label="Almoço início">
                      <input type="time" value={form.hora_almoco_ini}
                        onChange={e => setForm(f => ({ ...f, hora_almoco_ini: e.target.value }))}
                        style={inputStyle} />
                    </Campo>
                    <Campo label="Almoço fim">
                      <input type="time" value={form.hora_almoco_fim}
                        onChange={e => setForm(f => ({ ...f, hora_almoco_fim: e.target.value }))}
                        style={inputStyle} />
                    </Campo>
                  </div>
                  <Campo label="Observação">
                    <input value={form.observacao}
                      onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))}
                      style={inputStyle} placeholder="Opcional" />
                  </Campo>
                  <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                    <button onClick={cancelar} style={{
                      padding: '6px 14px', fontSize: '12px', fontWeight: '600',
                      border: `1px solid ${COR.borda}`, borderRadius: '5px',
                      backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
                    }}>Cancelar</button>
                    <button onClick={() => salvar(c)} style={{
                      padding: '6px 18px', fontSize: '12px', fontWeight: '600',
                      border: 'none', borderRadius: '5px',
                      backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
                    }}>💾 Salvar</button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: '10px', fontWeight: '600',
        color: COR.muted, marginBottom: '3px',
      }}>{label}</label>
      {children}
    </div>
  );
}

export default HorariosColaboradoresPage;
